import { Product } from '@features/catalog/models/product.models';
import { ProductCategory } from '@features/catalog/models/product-category.models';
import {
  PreparationEditorValue,
  PreparationEntityKind,
  PreparationSettingsApiInput,
  PreparationTimeKind,
  PrepRequiresMode,
} from './preparation-editor.models';

export interface PreparationLocationSource {
  prepRequires?: boolean | null;
  prepTimeMinutes?: number | null;
  fulfillmentLeadTimeDays?: number | null;
  fulfillmentReadyTime?: string | null;
}

export interface PreparationProductFormSource {
  sendToKitchen: boolean;
  prepRequiresMode?: PrepRequiresMode | null;
  prepTimeMinutes?: number | null;
  fulfillmentLeadTimeMode?: PrepRequiresMode | null;
  fulfillmentLeadTimeDays?: number | null;
  fulfillmentReadyTime?: string | null;
}

function inferTimeKind(source: {
  prepTimeMinutes?: number | null;
  fulfillmentLeadTimeMode?: PrepRequiresMode | null;
}): PreparationTimeKind {
  if (source.prepTimeMinutes != null && source.prepTimeMinutes > 0) {
    return 'minutes';
  }
  if (source.fulfillmentLeadTimeMode === 'override') {
    return 'days';
  }
  return 'inherit';
}

export function preparationEditorValueFromProduct(
  product: Pick<
    Product,
    | 'sendToKitchen'
    | 'prepRequiresMode'
    | 'prepTimeMinutes'
    | 'fulfillmentLeadTimeMode'
    | 'fulfillmentLeadTimeDays'
    | 'fulfillmentReadyTime'
  >
): PreparationEditorValue {
  const prepRequiresMode: PrepRequiresMode =
    product.prepRequiresMode === 'inherit' ? 'inherit' : 'override';

  return {
    requiresPrep: product.sendToKitchen ?? false,
    prepRequiresMode,
    timeKind: inferTimeKind({
      prepTimeMinutes: product.prepTimeMinutes,
      fulfillmentLeadTimeMode: product.fulfillmentLeadTimeMode,
    }),
    prepTimeMinutes: product.prepTimeMinutes ?? null,
    fulfillmentLeadTimeDays: product.fulfillmentLeadTimeDays ?? 0,
    fulfillmentReadyTime: product.fulfillmentReadyTime ?? null,
  };
}

export function preparationEditorValueFromCategory(
  category: Pick<
    ProductCategory,
    | 'prepRequires'
    | 'prepRequiresMode'
    | 'prepTimeMinutes'
    | 'fulfillmentLeadTimeMode'
    | 'fulfillmentLeadTimeDays'
  >
): PreparationEditorValue {
  const prepRequiresMode: PrepRequiresMode =
    category.prepRequiresMode === 'override' ? 'override' : 'inherit';

  return {
    requiresPrep: category.prepRequires ?? true,
    prepRequiresMode,
    timeKind: inferTimeKind({
      prepTimeMinutes: category.prepTimeMinutes,
      fulfillmentLeadTimeMode: category.fulfillmentLeadTimeMode,
    }),
    prepTimeMinutes: category.prepTimeMinutes ?? null,
    fulfillmentLeadTimeDays: category.fulfillmentLeadTimeDays ?? 0,
    fulfillmentReadyTime: null,
  };
}

export function preparationEditorValueFromLocation(
  location: PreparationLocationSource
): PreparationEditorValue {
  const hasMinutes = location.prepTimeMinutes != null && location.prepTimeMinutes > 0;
  const hasDays =
    location.fulfillmentLeadTimeDays != null && location.fulfillmentLeadTimeDays > 0;

  let timeKind: PreparationTimeKind = 'inherit';
  if (hasMinutes) {
    timeKind = 'minutes';
  } else if (hasDays) {
    timeKind = 'days';
  }

  return {
    requiresPrep: location.prepRequires ?? true,
    prepRequiresMode: null,
    timeKind,
    prepTimeMinutes: location.prepTimeMinutes ?? null,
    fulfillmentLeadTimeDays: location.fulfillmentLeadTimeDays ?? 0,
    fulfillmentReadyTime: location.fulfillmentReadyTime ?? null,
  };
}

export function preparationEditorValueFromForm(
  raw: PreparationProductFormSource
): PreparationEditorValue {
  const prepRequiresMode: PrepRequiresMode =
    raw.prepRequiresMode === 'inherit' ? 'inherit' : 'override';

  return {
    requiresPrep: raw.sendToKitchen ?? false,
    prepRequiresMode,
    timeKind: inferTimeKind({
      prepTimeMinutes: raw.prepTimeMinutes,
      fulfillmentLeadTimeMode: raw.fulfillmentLeadTimeMode,
    }),
    prepTimeMinutes: raw.prepTimeMinutes ?? null,
    fulfillmentLeadTimeDays: raw.fulfillmentLeadTimeDays ?? 0,
    fulfillmentReadyTime: raw.fulfillmentReadyTime ?? null,
  };
}

export function applyPreparationEditorValueToForm(
  value: PreparationEditorValue
): PreparationProductFormSource {
  const fulfillmentLeadTimeMode: PrepRequiresMode =
    value.timeKind === 'days' ? 'override' : 'inherit';

  return {
    sendToKitchen: value.requiresPrep,
    prepRequiresMode: value.prepRequiresMode ?? 'override',
    prepTimeMinutes: value.timeKind === 'minutes' ? value.prepTimeMinutes : null,
    fulfillmentLeadTimeMode,
    fulfillmentLeadTimeDays:
      value.timeKind === 'days' ? (value.fulfillmentLeadTimeDays ?? 0) : null,
    fulfillmentReadyTime:
      value.timeKind === 'days' ? (value.fulfillmentReadyTime?.trim() || null) : null,
  };
}

export function mapPreparationEditorValueToApiInput(
  value: PreparationEditorValue,
  options: { entityKind: PreparationEntityKind }
): PreparationSettingsApiInput {
  const input: PreparationSettingsApiInput = {
    timeKind: value.timeKind,
  };

  if (options.entityKind === 'location') {
    input.requiresPrep = value.requiresPrep;
  } else if (value.prepRequiresMode === 'override') {
    input.requiresPrep = value.requiresPrep;
    input.prepRequiresMode = 'override';
  } else {
    input.prepRequiresMode = 'inherit';
  }

  if (value.timeKind === 'minutes') {
    input.prepTimeMinutes = value.prepTimeMinutes;
  } else if (value.timeKind === 'days') {
    input.prepTimeMinutes = null;
    input.fulfillmentLeadTimeDays = value.fulfillmentLeadTimeDays ?? 0;
    input.fulfillmentReadyTime = value.fulfillmentReadyTime?.trim().slice(0, 5) || null;
  } else {
    input.prepTimeMinutes = null;
  }

  return input;
}

/** Campos legacy de bundle/create para productos (sin `timeKind` en GraphQL bundle). */
export function mapPreparationEditorValueToProductBundleFields(
  value: PreparationEditorValue,
  fulfillmentLeadTimeEnabled: boolean
): {
  sendToKitchen?: boolean;
  prepRequiresMode?: PrepRequiresMode;
  prepTimeMinutes?: number | null;
  fulfillmentLeadTimeMode?: PrepRequiresMode;
  fulfillmentLeadTimeDays?: number | null;
  fulfillmentReadyTime?: string | null;
} {
  if (!fulfillmentLeadTimeEnabled) {
    // Sin módulo: no enviar inherit (dispara reset de anticipación en backend) ni campos de lead time.
    return {
      sendToKitchen: value.requiresPrep,
      prepRequiresMode: 'override',
      prepTimeMinutes: value.timeKind === 'minutes' ? value.prepTimeMinutes : null,
    };
  }

  const form = applyPreparationEditorValueToForm(value);
  return {
    sendToKitchen: form.sendToKitchen,
    prepRequiresMode: form.prepRequiresMode ?? undefined,
    prepTimeMinutes: form.prepTimeMinutes,
    fulfillmentLeadTimeMode: form.fulfillmentLeadTimeMode ?? undefined,
    fulfillmentLeadTimeDays: form.fulfillmentLeadTimeDays,
    fulfillmentReadyTime: form.fulfillmentReadyTime,
  };
}

export function mapPreparationApiInputToCategoryMutationFields(
  input: PreparationSettingsApiInput
): {
  prepRequiresMode?: PrepRequiresMode | null;
  prepRequires?: boolean | null;
  prepTimeMinutes?: number | null;
  fulfillmentLeadTimeMode?: PrepRequiresMode | null;
  fulfillmentLeadTimeDays?: number | null;
} {
  const fields: ReturnType<typeof mapPreparationApiInputToCategoryMutationFields> = {};

  if (input.prepRequiresMode !== undefined) {
    fields.prepRequiresMode = input.prepRequiresMode;
  }
  if (input.requiresPrep !== undefined) {
    fields.prepRequires = input.requiresPrep;
  }
  if (input.prepTimeMinutes !== undefined) {
    fields.prepTimeMinutes = input.prepTimeMinutes;
  }

  if (input.timeKind === 'days') {
    fields.fulfillmentLeadTimeMode = 'override';
    fields.fulfillmentLeadTimeDays = input.fulfillmentLeadTimeDays ?? 0;
  } else if (input.timeKind === 'inherit') {
    fields.fulfillmentLeadTimeMode = 'inherit';
    fields.fulfillmentLeadTimeDays = null;
  } else if (input.timeKind === 'minutes') {
    fields.fulfillmentLeadTimeMode = 'inherit';
    fields.fulfillmentLeadTimeDays = null;
  }

  return fields;
}
